import {
  getFlagListPageSize,
  isABTestingEnabled,
  isCodeReferencesEnabled,
  isDisableCodeReferencesWithOverrideEnabled,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { fromJS, Record as ImmutableRecord, List, Set } from '@gonfalon/immutable-shim/immutableShim';
import type { CreateFunctionInput } from '@gonfalon/legacy-immutable/immutableUtils';
import { isEmpty } from 'lodash';
import qs from 'qs';

import type { FilterValue } from '#components/ui/multipleFilters/types';
import { FilterKind } from '#components/ui/multipleFilters/types';
import { ContextKindsEvaluatedFilter } from '#utils/flagFilters/contextKindsEvaluatedFilter';
import { ContextKindTargetedFilter } from '#utils/flagFilters/contextKindTargetedFilter';
import { CreationDateFlagFilter } from '#utils/flagFilters/creationDateFilter';
import { StatusFilter } from '#utils/flagFilters/statusFilter';
import { TagsFilter } from '#utils/flagFilters/tagsFilter';
import { TargetingFilter } from '#utils/flagFilters/targetingFilter';
import { TypeFilter } from '#utils/flagFilters/typeFilter';

import { ApplicationEvaluatedFilter } from '../applicationEvaluatedFilter';
import { clearFilterEnvIfNeeded } from '../clearFilterEnvIfNeeded';
import { AdditionalFilterOption, type FlagSortOrder } from '../constants';
import { evaluatedOptions } from '../evaluatedOptions';
import { flagSdkOptions } from '../flagSdkOptions';
import { createdSortFilters, nameSortFilters, targetingModifiedSortFilterOptions } from '../getSortFilterOptions';
import { initialFlagSortOrder } from '../initialFlagSortOrder';
import { removeInvalidFilters } from '../removeInvalidFilters';
import { SegmentTargetedFilter } from '../segmentTargetedFilter';
import { StateFilter } from '../stateFilter';
import type {
  CodeReferencesFlagFilter,
  EvaluatedRanges,
  FilterDisplayType,
  FlagFilterProps,
  FlagFiltersQuery,
  FlagFiltersRecord,
} from '../types';

const archivedSortFilters = {
  '-archivedDate': 'Archived newest',
  archivedDate: 'Archived oldest',
};

const sortFilterDisplayNames: Record<FlagSortOrder, string> = {
  ...createdSortFilters,
  ...archivedSortFilters,
  ...nameSortFilters,
  ...targetingModifiedSortFilterOptions,
};

export class FlagFilters extends ImmutableRecord<FlagFiltersRecord>({
  q: '',
  sort: '',
  tags: Set(),
  type: undefined,
  maintainerId: '',
  maintainerTeamKey: '',
  hasExperiment: undefined,
  hasDataExport: undefined, // deprecated filter (v4 of the API)
  evaluated: undefined,
  status: '',
  sdkAvailability: '',
  filterEnv: '',
  settings: List(),
  archived: false,
  offset: 0,
  limit: -1,
  targeting: '',
  codeReferences: undefined,
  followerId: '',
  creationDate: undefined,
  contextKindTargeted: '',
  segmentTargeted: '',
  contextKindsEvaluated: Set(),
  applicationEvaluated: '',
  state: undefined,
  purpose: undefined,
}) {
  getFilters(): [
    CreationDateFlagFilter,
    StatusFilter,
    TagsFilter,
    TargetingFilter,
    TypeFilter,
    ContextKindTargetedFilter,
    ContextKindsEvaluatedFilter,
    SegmentTargetedFilter,
    ApplicationEvaluatedFilter,
    StateFilter,
  ] {
    return [
      new CreationDateFlagFilter(this.creationDate),
      new StatusFilter(this.status),
      new TagsFilter(this.tags),
      new TargetingFilter(this.targeting),
      new TypeFilter(this.type),
      new ContextKindTargetedFilter(this.contextKindTargeted),
      new ContextKindsEvaluatedFilter(this.contextKindsEvaluated),
      new SegmentTargetedFilter(this.segmentTargeted),
      new ApplicationEvaluatedFilter(this.applicationEvaluated),
      new StateFilter(this.state),
    ];
  }

  isEmpty() {
    return isEmpty(this.q) && this.settings.isEmpty() && this.areFiltersEmpty();
  }

  toQuery(flagCount: number = 0, omitPaginationParams: boolean = false, alwaysIncludeSort: boolean = false) {
    let ret: FlagFiltersQuery = {};

    const validFilters = removeInvalidFilters(this);

    if (!isEmpty(this.q)) {
      ret.q = this.q;
    }

    if (isABTestingEnabled() && this.hasExperiment !== undefined) {
      ret.hasExperiment = this.hasExperiment.toString();
    }

    if (
      !isDisableCodeReferencesWithOverrideEnabled() &&
      isCodeReferencesEnabled() &&
      this.codeReferences !== undefined
    ) {
      if (this.codeReferences?.get('min') === 1) {
        ret.codeReferences = 'true';
      } else if (this.codeReferences?.get('max') === 0) {
        ret.codeReferences = 'false';
      }
    }

    if (validFilters.evaluated) {
      ret.evaluated = validFilters.evaluated;
    }

    if (!isEmpty(this.maintainerId)) {
      ret.maintainerId = this.maintainerId;
    }

    if (!isEmpty(this.maintainerTeamKey)) {
      ret.maintainerTeamKey = this.maintainerTeamKey;
    }

    if (!this.settings.isEmpty()) {
      ret.settings = this.settings.toArray();
    }

    if (!isEmpty(validFilters.sdkAvailability)) {
      ret.sdkAvailability = validFilters.sdkAvailability;
    }

    if (!isEmpty(this.filterEnv)) {
      ret.filterEnv = this.filterEnv;
    }

    if (!isEmpty(this.sort) && (this.sort !== initialFlagSortOrder() || alwaysIncludeSort)) {
      ret.sort = this.sort;
    }

    if (this.archived) {
      ret.archived = this.archived.toString();
    }

    if (!isEmpty(this.segmentTargeted)) {
      ret.segmentTargeted = this.segmentTargeted;
    }

    if (!isEmpty(this.applicationEvaluated)) {
      ret.applicationEvaluated = this.applicationEvaluated;
    }

    if (!isEmpty(this.state)) {
      ret.state = this.state;
    }

    const filters = validFilters.getFilters();
    filters.forEach((filter) => {
      if (filter.isEnabled() && !filter.isEmpty()) {
        ret = {
          ...ret,
          [filter.queryParamName]: filter.toQuery(),
        };
      }
    });

    if (omitPaginationParams) {
      return ret;
    }

    if (this.limit !== -1 && (this.limit !== getFlagListPageSize() || !!this.offset)) {
      ret.limit = this.limit.toString();
    }

    if (this.offset && flagCount > this.offset) {
      ret.offset = this.offset.toString();
    }

    return ret;
  }

  areFiltersEmpty() {
    const filters = this.getFilters();
    for (const filter of filters) {
      if (filter.isEnabled() && !filter.isEmpty()) {
        return false;
      }
    }

    return (
      (isEmpty(this.sort) || this.sort === initialFlagSortOrder()) &&
      isEmpty(this.maintainerId) &&
      isEmpty(this.maintainerTeamKey) &&
      isEmpty(this.sdkAvailability) &&
      this.evaluated === undefined &&
      (isABTestingEnabled() ? this.hasExperiment === undefined : true) &&
      (!isDisableCodeReferencesWithOverrideEnabled() && isCodeReferencesEnabled()
        ? this.codeReferences === undefined
        : true)
    );
  }

  getActiveFilters() {
    let ret: FlagFilterProps = {};

    if (!isEmpty(this.maintainerId)) {
      ret.maintainerId = this.maintainerId;
    }

    if (!isEmpty(this.maintainerTeamKey)) {
      ret.maintainerTeamKey = this.maintainerTeamKey;
    }

    if (!isEmpty(this.sdkAvailability)) {
      ret.sdkAvailability = this.sdkAvailability;
    }

    if (this.evaluated !== undefined) {
      ret.evaluated = this.evaluated;
    }

    if (isABTestingEnabled() && this.hasExperiment !== undefined) {
      ret.hasExperiment = this.hasExperiment;
    }

    if (
      !isDisableCodeReferencesWithOverrideEnabled() &&
      isCodeReferencesEnabled() &&
      this.codeReferences !== undefined
    ) {
      ret.codeReferences = this.codeReferences;
    }

    if (!isEmpty(this.state)) {
      ret.state = this.state;
    }

    const filters = this.getFilters();

    filters.forEach((filter) => {
      if (filter.isEnabled() && !filter.isEmpty()) {
        ret = {
          ...ret,
          [filter.backendQueryParamName]: filter.toQuery(),
        };
      }
    });

    return ret;
  }

  getAllActiveFiltersExcludingPagination() {
    const activeFilters = this.getActiveFilters();
    if (!isEmpty(this.sort)) {
      activeFilters.sort = this.sort;
    }

    if (!isEmpty(this.q)) {
      activeFilters.q = this.q;
    }

    return activeFilters;
  }

  toQueryString(flagCount: number = 0, omitPaginationParams: boolean = false, alwaysIncludeSort: boolean = false) {
    return qs.stringify(this.toQuery(flagCount, omitPaginationParams, alwaysIncludeSort), { indices: false });
  }

  clearFilter(filterCleared: keyof FlagFilterProps) {
    const updatedFilters = this.delete(filterCleared);
    return clearFilterEnvIfNeeded(updatedFilters);
  }

  maybeAddFilterEnv(envKey: string) {
    if (!isEmpty(this.status) || this.evaluated !== undefined) {
      return this.set(AdditionalFilterOption.ENVIRONMENT, envKey);
    }
    return this;
  }

  getFilter(kind: FilterKind) {
    const filters = this.getFilters();

    return filters.find((f) => f.kind === kind);
  }

  getFilterData(kind: FilterKind) {
    const filter = this.getFilter(kind);
    if (!filter) {
      return undefined;
    }
    return filter.getFilterData();
  }

  getOptions(kind: FilterKind) {
    const filter = this.getFilter(kind);
    if (!filter) {
      return [];
    }
    return filter.getOptions();
  }

  getFilterValueDisplayName(filterKind: FilterDisplayType, filterValue?: FilterValue) {
    const filter = this.getFilter(filterKind as FilterKind);
    if (filter) {
      return filter.getFilterValueDisplayName(filterValue);
    }
    switch (filterKind) {
      case FilterKind.SDKAVAILABILITY:
        return flagSdkOptions[filterValue as keyof typeof flagSdkOptions];
      case FilterKind.CODE_REFERENCES:
        const value = filterValue as CodeReferencesFlagFilter;
        return value?.get('min') !== undefined ? 'Yes' : 'No';
      case FilterKind.DATA_EXPORT:
      case FilterKind.EXPERIMENT:
        return filterValue ? 'Yes' : 'No';
      case FilterKind.EVALUATED:
        return evaluatedOptions[filterValue as EvaluatedRanges];
      case AdditionalFilterOption.SORT:
        return sortFilterDisplayNames[filterValue as FlagSortOrder];
      default:
        return filterValue;
    }
  }
}

export function createFlagFilters(props: CreateFunctionInput<FlagFilters> = {}) {
  const filters = props instanceof FlagFilters ? props : new FlagFilters(fromJS(props));
  return filters.withMutations((f) => {
    f.update('tags', (ts) => ts.toSet());
    f.update('contextKindsEvaluated', (ts) => ts.toSet());
  });
}
